import { LeadPriorityScore } from "app/pages/sales-intelligence/domain/leads/types/lead-priority";
import type { TeamLeadScoringCriteriaRules } from "app/pages/sales-intelligence/modules/teams/types/teams.types";

/** The rules a team has selected, each mapped to the priority column it was picked from. */
export type TeamScoringSelection = ReadonlyMap<string, LeadPriorityScore>;

export const areSelectionsEqual = (a: TeamScoringSelection, b: TeamScoringSelection): boolean =>
    a.size === b.size && [...a].every(([ruleId, priority]) => b.get(ruleId) === priority);

export const toggleRule = (
    selection: TeamScoringSelection,
    ruleId: string,
    priority: LeadPriorityScore,
): TeamScoringSelection => {
    const next = new Map(selection);

    if (!next.delete(ruleId)) {
        next.set(ruleId, priority);
    }

    return next;
};

export const omitRules = (selection: TeamScoringSelection, ruleIds: ReadonlySet<string>): TeamScoringSelection => {
    const next = new Map(selection);

    ruleIds.forEach((ruleId) => next.delete(ruleId));

    return next.size === selection.size ? selection : next;
};

export const removeRule = (selection: TeamScoringSelection, ruleId: string): TeamScoringSelection => {
    if (!selection.has(ruleId)) {
        return selection;
    }

    const next = new Map(selection);
    next.delete(ruleId);

    return next;
};

const ruleIdsForPriority = (selection: TeamScoringSelection, priority: LeadPriorityScore): string[] =>
    [...selection].filter(([, rulePriority]) => rulePriority === priority).map(([ruleId]) => ruleId);

export const buildLeadScoringCriteriaRules = (selection: TeamScoringSelection): TeamLeadScoringCriteriaRules => ({
    [LeadPriorityScore.High]: ruleIdsForPriority(selection, LeadPriorityScore.High),
    [LeadPriorityScore.Medium]: ruleIdsForPriority(selection, LeadPriorityScore.Medium),
    [LeadPriorityScore.Low]: ruleIdsForPriority(selection, LeadPriorityScore.Low),
});
